<template lang="pug">
section.section(
    :class="classes"
    @click="editSection"
)
    include ../base.pug
    .content-wrappers(v-if="!isEmpty || $slots.default")
        slot
        .content-wrapper(
            v-for="config in contents"
            :key="config.key"
        )
            input-contents(
                :section="section"
                :config="config"
                :options="options"
                :section-settings="settings"
                :section-setting-values="settingValues"
                :edit="false"
            )
    el-drawer.full-height-drawer.visible-overflow(
        v-if="editable"
        :title="$t('Edit {section}', { section: section.name })"
        :visible.sync="scopedUI.form"
        :close-on-press-escape="true"
        :wrapper-closable="false"
        :append-to-body="true"
        size="920px"
        :modal="true"
        @closed="resetDrawer"
    )
        .drawer-body(v-loading="drawer.loading")
            el-tabs(
                v-if="!drawer.loading"
                v-model="drawer.tab"
                tab-position="top"
            )
                el-tab-pane(name="content" :label="$t('Form')")
                    .tab-content(v-if="contents && contents.length")
                        .content-wrapper(
                            v-for="config in contents"
                            v-if="checkConditions(config)"
                            :key="config.key"
                        )
                            input-contents(
                                :section="drawer.section"
                                :config="config"
                                :options="options"
                                :section-settings="settings"
                                :section-setting-values="settingValues"
                                :edit="true"
                            )
                el-tab-pane(name="settings" :label="$t('Settings')")
                    .block-settings
                        .settings-wrap
                            include ../settings.pug
        .drawer-footer
            include ../section-drawer-footer.pug
</template>

<i18n>
{
    "en": {
        "email-intro-text": "<p>Thank you for your contact request.<br>The following data has been submitted:</p>",
        "Edit {section}": "Edit {section}",
        "Your contact request at {project}": "Your contact request at {project}"
    },
    "de": {
        "Edit {section}": "{section} bearbeiten",
        "Content": "Inhalt",
        "Form": "Formular",
        "Settings": "Einstellungen",
        "Defined in the email template": "In der E-Mail-Vorlage definiert",
        "Owner email (default)": "Inhaber-E-Mail (Standard)",
        "Hello,": "Hallo,",
        "Input field": "Eingabefeld",
        "To": "Empfänger",
        "Cc": "Cc",
        "Bcc": "Bcc",
        "Subject": "Betreff",
        "Your contact request at {project}": "Ihre Kontaktanfrage bei {project}",
        "Heading": "Überschrift",
        "Text": "Text",
        "Recipient": "Empfänger",
        "Email intro": "Email Einleitung",
        "email-intro-text": "<p>vielen Dank für deine Kontaktanfrage.<br>Folgende Daten wurden erfasst:</p>",
        "Email address": "Email-Adresse",
        "from": "ab",
        "to": "bis",
        "Margin top": "Abstand oben",
        "Margin bottom": "Abstand unten",
        "none": "Ohne",
        "small": "Klein",
        "medium": "Mittel",
        "large": "Groß",
        "default": "Standard",
        "custom": "Benutzerdefiniert",
        "e.g. 40px, 10%, 2em, ...": "z.B. 40px, 10%, 2em, ...",
        "desktop": "Desktop",
        "tablet": "Tablet",
        "mobile": "Telefon",
        "Visible on": "Anzeigen auf",
        "Element is displayed on": "Inhaltselement <br> wird dargestellt auf"
    }
}
</i18n>

<script>

import _ from 'lodash'
import uniqid from 'uniqid'

import {
    set,
    isTokenValidOrUndefined,
    fetchAccessToken,
    addContentToSection,
    sortContents,
} from '@/helpers'

import store from '@/store'
import Section from '@/components/Page/Sections/Section'

import InputContents from './Partials/InputContents'

export default {
    name: 'Form',
    extends: Section,
    components: {
        InputContents,
    },
    computed: {
        editable() {
            return true;
        },
        additionalContents() {
            let newContents = []
            const section = _.get(this.drawer, 'section') || this.section
            section.contents.forEach(content => {
                if (content.key && _.find(newContents, ['key', content.key]) === undefined
                    && _.find(this.contents, ['key', content.key]) === undefined) {
                    newContents.push(content)
                }
            })
            return newContents
        },
        requiredContents() {
            return {
                emailIntro: {
                    key: 'email-intro',
                    name: this.$t('Email intro'),
                    position: 1,
                    fields: [
                        {
                            key: 'title',
                            name: this.$t('Email intro'),
                            placeholder: this.$t('Heading'),
                            value: this.$t('Hello,'),
                            type: 'textfield',
                        },
                        {
                            key: 'text',
                            placeholder: this.$t('Text'),
                            value: this.$t('email-intro-text'),
                            type: 'rte',
                        }
                    ],
                },
                nameField: {
                    key: 'user-name',
                    name: this.$t('Name'),
                    position: 2,
                    fields: [
                        {
                            key: 'title',
                            name: 'Formularfeld',
                            value: this.$t('Name'),
                            type: 'textfield',
                        }
                    ],
                },
                emailField: {
                    key: 'user-email',
                    name: this.$t('Email address'),
                    position: 3,
                    fields: [
                        {
                            key: 'title',
                            name: 'Formularfeld',
                            value: this.$t('Email address'),
                            type: 'textfield',
                        }
                    ],
                },
            }
        },
        contents() {
            let contents = []

            const section = _.get(this.drawer, 'section') || this.section
            section.contents.forEach(content => {
                switch (content.key) {
                    case 'email-intro':
                        contents.push(this.requiredContents.emailIntro)
                        break;

                    case 'user-email':
                        contents.push(this.requiredContents.emailField)
                        break;

                    case 'user-name':
                        contents.push(this.requiredContents.nameField)
                        break;
                }
            })

            const configs = _.get(this.defaultConfig, 'contents') || []
            configs.forEach(config => {
                const sorted = _.orderBy(contents, 'position', 'desc')

                let position = 1
                if (sorted.length) position = sorted[0].position + 1

                let content = _.cloneDeep(config)
                content.position = position

                // content.settings.forEach(setting => {
                //     if (setting.key === 'type') {
                //         setting.options = setting.options.filter(el => {
                //             if (el.key === 'newsletter-checkbox' && !this.enabledExtensions.includes('newsletter')) return false
                //             return true
                //         })
                //     }
                // })

                contents.push(content)
            })

            return contents.sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))
        },
        sendEmail() {
            let sendEmail

            const section = _.get(this.drawer, 'section') || this.section

            const sections = _.get(this.activeCms, 'config.template.sections') || []
            const config = sections.find(el => el.key === section.key)

            if (config.settings && config.settings.length) {
                let setting = config.settings.find(el => el.key === 'send-email')
                const defaultValue = _.get(setting, 'value') || false

                const settings = _.get(section, 'settings') || []
                setting = settings.find(el => el.key === 'send-email')
                sendEmail = _.get(setting, 'value', defaultValue)
            }

            return ['true', '1', 'yes', true].includes(sendEmail)
        },
        // config() {
        //     const sections = _.get(this.activeCms, 'config.template.sections', [])
        //     let section = sections.find(section => section.key === this.section.key)
        //
        //     section = _.cloneDeep(section)
        //
        //     // check if 'send-email' switch is checked
        //     // add email related fields
        //
        //     if (this.sendEmail) {
        //         // section.contents.push()
        //         console.log('foooooooo');
        //     }
        //
        //     return section
        // },
        // The notification settings now come straight from the section YAML
        // (no more dynamic splicing). We only enrich them with dynamic
        // placeholders that hint at the fallback behaviour.
        settings() {
            let settings = _.cloneDeep(
                _.get(this.sectionConfig, 'settings') || _.get(this.defaultConfig, 'settings') || []
            )

            // make divider keys unique (mirrors the base Section behaviour)
            settings.forEach((setting, index) => {
                if (setting.type === 'divider') setting.key = `${setting.key}-${index}`
            })

            // Hint at the fallback behaviour without leaking data the admin
            // payload doesn't carry: when no recipient is entered the backend
            // sends to the CMS owner; the subject falls back to the email template.
            const ownerEmail = _.get(this.activeCms, 'owner.user.email')
            settings.forEach(setting => {
                if (setting.key === 'to' && !setting.placeholder) {
                    setting.placeholder = ownerEmail || this.$t('Owner email (default)')
                }
                if (setting.key === 'subject' && !setting.placeholder) {
                    setting.placeholder = this.$t('Defined in the email template')
                }
            })

            return settings
        },
    },
    methods: {
        // Seed the YAML-defined default fields (Name / Email / Message) into a
        // freshly added form so it is usable out of the box. Creation-only: never
        // touches a form that already has fields.
        ensureDefaultContents() {
            const section = _.get(this.drawer, 'section') || this.section
            if (!section) return

            const existing = (section.contents || []).filter(content => !content.deleted)
            if (existing.length) return

            const defaults = _.get(this.defaultConfig, 'defaults') || []
            if (!defaults.length) return

            if (!section.contents) this.$set(section, 'contents', [])

            defaults.forEach((def, index) => {
                section.contents.push({
                    id: uniqid('new:'),
                    key: def.key,
                    title: def.title || '',
                    text: '',
                    position: index + 1,
                    files: [],
                    images: [],
                    categories: [],
                    hyperlink: {},
                    settings: _.cloneDeep(def.settings || []),
                })
            })

            // Seed the notification toggles explicitly so a new form works out of
            // the box AND the two toggles are independently controllable (the
            // backend only treats them as independent when the values are stored).
            if (!section.settings) this.$set(section, 'settings', [])
            const ensureSetting = (key, value) => {
                if (!section.settings.some(setting => setting.key === key)) {
                    section.settings.push({ key, value })
                }
            }
            ensureSetting('send-copy-to', true)
            ensureSetting('send-email', true)
        },
    },
    mounted() {
        this.ensureDefaultContents()
    }
}
</script>
